import type { DeductibleSource } from "@/resources/claimants/hooks/useCurrentPolicyData";
import { formatUSD } from "@/utils/formatters/currencyFormatter";

/**
 * Copy under "Claim amount" (#7582 AC1), e.g.
 *   "$86,905 over the $150,000 lasered deductible · $24,425 already on 1 other claim on this policy"
 *
 * Wording rules (calc-expert review, 2026-10-23):
 * - never "Total paid": the stored amount is the netted claim amount;
 * - netting is per claimant × policy, every claim type, vendor and status
 *   (`SumStoplossClaimsByClaimantAndPolicy`; ADR-081 D3 correction), so "other
 *   claims on this policy", not "earlier" and not "this policy year" (terms are
 *   not always 12 months);
 * - the deductible word comes from the resolved source, not from a laser flag:
 *   a contract laser or a modified deductible is not "lasered".
 */
export const whole = (n: number) => formatUSD(n, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

/** Adjective for the applied deductible, by its resolved source. Empty for the base deductible. */
export const deductibleWord = (source: DeductibleSource | undefined): string => {
  switch (source) {
    case "LASER":
      return "lasered";
    case "CONTRACT":
      return "contract";
    case "CONDITIONAL":
      return "conditional-laser";
    case "MODIFICATION":
      return "modified";
    case "REVIEW_DECISION":
      return "reviewed";
    default:
      return "";
  }
};

/** A caption clause split around the word(s) that open a peek: `before` + [link] + `after`. */
export interface LinkedClause {
  before: string;
  link: string;
  after: string;
}

export const otherClaimsParts = (prior: number, count: number | undefined, frozen: boolean): LinkedClause | null => {
  const n = count && count > 0 ? count : 0;
  const claims = (k: number) => `${k} other claim${k === 1 ? "" : "s"}`;
  if (frozen) {
    // `total_prior_submissions_ytd` is written at insert only (no backfill on amend),
    // so it is "as recorded when the claim was created", not "what the amount was netted against".
    if (prior > 0)
      return {
        before: `${whole(prior)} on `,
        link: "other claims",
        after: ", as recorded when this claim was created",
      };
    // Filed with no prior on record (a manual create, or pre-#7579 data): still point at the siblings.
    return n > 0 ? { before: "", link: claims(n), after: " on this policy" } : null;
  }
  if (!(prior > 0)) return null;
  return { before: `${whole(prior)} already on `, link: claims(Math.max(n, 1)), after: " on this policy" };
};

export const otherClaimsPhrase = (prior: number, count: number | undefined, frozen: boolean): string | null => {
  const p = otherClaimsParts(prior, count, frozen);
  return p ? `${p.before}${p.link}${p.after}` : null;
};

/**
 * "$X over the $Y [lasered] deductible": the source word is the link to the
 * deductible peek; for a base deductible the word "deductible" takes the link.
 * An excluded claimant gets the sentence alone, with "Excluded" as the link.
 */
export const overDeductibleParts = (
  spendOver: number | undefined,
  deductible: number | undefined,
  source: DeductibleSource | undefined
): LinkedClause | null => {
  if (source === "EXCLUDED") return { before: "", link: "Excluded", after: " from the contract" };
  if (spendOver === undefined || deductible === undefined) return null;
  const word = deductibleWord(source);
  return word
    ? { before: `${whole(spendOver)} over the ${whole(deductible)} `, link: word, after: " deductible" }
    : { before: `${whole(spendOver)} over the ${whole(deductible)} `, link: "deductible", after: "" };
};

export const overDeductiblePhrase = (
  spendOver: number | undefined,
  deductible: number | undefined,
  source: DeductibleSource | undefined
): string | null => {
  const p = overDeductibleParts(spendOver, deductible, source);
  return p ? `${p.before}${p.link}${p.after}` : null;
};
